<template>
    <div class="flex flex-col gap-1 w-full">
        <div v-if="!notes.length && !canAdd">--</div>
        <div v-for="note in shownNotes" :key="note.id">
            <div class="flex gap-2 text-xs text-gray-500">
                <span>{{ formatFeedbackDate(note.created_at) }}</span>
                <span v-if="isFromAnotherCampaign(note, company)">({{ note.campaign.campaign_name }})</span>
                <template v-if="editable && editingId !== note.id">
                    <button type="button" class="hover:underline" @click.stop="startEdit(note)">Edit</button>
                    <button type="button" class="hover:underline text-red-700" @click.stop="remove(note)">Delete</button>
                </template>
            </div>
            <textarea
                v-if="editingId === note.id"
                v-model="draft"
                @click.stop
                @keydown.enter.exact.prevent="saveEdit(note)"
                @keydown.esc="editingId = null"
                rows="3"
                :class="textareaClass"
            />
            <div v-else class="whitespace-pre-wrap break-words" :class="{ 'line-clamp-2': clamp }">{{ note.notes || '--' }}</div>
        </div>
        <div v-if="hiddenCount > 0" class="text-xs text-gray-500">+{{ hiddenCount }} more</div>
        <textarea
            v-if="canAdd"
            v-model="newNote"
            @click.stop
            @keydown.enter.exact.prevent="add"
            rows="2"
            placeholder="Add a note (Enter to save, Shift+Enter for a new line)"
            :class="textareaClass"
        />
    </div>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import {
    createCompanyClientFeedbackNote, updateCompanyClientFeedbackNote, deleteCompanyClientFeedbackNote,
    clientFeedbackErrorMessage,
} from '@/services/Company';
import { formatFeedbackDate, isFromAnotherCampaign } from '@/utils/clientFeedback';

// Every note for the client of the company's current campaign, newest first, as the
// backend sends them in current_client_feedback_notes.
const props = defineProps({
    company: Object,
    // Show only this many of the newest notes, with a count of the rest
    limit: { type: Number, default: null },
    // Clamp each note to two lines
    clamp: { type: Boolean, default: false },
    // Offer edit and delete on each note, and a box to add one
    editable: { type: Boolean, default: true },
    // With editable, whether to show the add box; a form with its own add box turns it off
    allowAdd: { type: Boolean, default: true },
})

const emit = defineEmits(['refetch'])

const store = useStore()
const $toast = useToast()

const textareaClass = 'w-full border border-gray-300 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400'

const notes = computed(() => props.company.current_client_feedback_notes ?? [])
const shownNotes = computed(() => props.limit ? notes.value.slice(0, props.limit) : notes.value)
const hiddenCount = computed(() => notes.value.length - shownNotes.value.length)
// Notes are recorded against the company's campaign, so there is nowhere to add one without it
const canAdd = computed(() => props.editable && props.allowAdd && Boolean(props.company.campaign))

const editingId = ref(null)
const draft = ref('')
const newNote = ref('')

function showError(result) {
    $toast.open({ type: 'error', duration: 10000, message: clientFeedbackErrorMessage(result) })
}

async function add() {
    if (!newNote.value.trim()) return
    const result = await createCompanyClientFeedbackNote(props.company.id, JSON.stringify({ notes: newNote.value }), store.state.auth.token)
    if (!result || result.status !== 201) {
        showError(result)
        return
    }
    newNote.value = ''
    emit('refetch')
}

function startEdit(note) {
    editingId.value = note.id
    draft.value = note.notes ?? ''
}

async function saveEdit(note) {
    const result = await updateCompanyClientFeedbackNote(note.id, draft.value, store.state.auth.token)
    if (!result || result.status !== 200) {
        showError(result)
        return
    }
    editingId.value = null
    emit('refetch')
}

async function remove(note) {
    if (!confirm('Delete this note?')) return
    const result = await deleteCompanyClientFeedbackNote(note.id, store.state.auth.token)
    if (!result || result.status !== 204) {
        showError(result)
        return
    }
    emit('refetch')
}
</script>
